// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { typeSensor, TypeSensorDO } from '../../requests';

import Variables from './variables/List';
import Edit from './Edit';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: TypeSensorDO[];
}

export default class List extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM']),
      del: hasLevel('role', ['ADM']),
      edit: true,
      list: true,
      labels: {
        list: 'Variables',
      }
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      showGrid: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    this.loadingStart();
    typeSensor<TypeSensorDO[]>('list')
      ?.then((resp: TypeSensorDO[]) => {
        this.setLocalState({ data: resp });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'STE';
    const title: string = rowId ? `Sensor-Type Edit: ${rowId}` : 'Sensor-Type New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'SENSOR TYPES',
        title: 'Sensor Types',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (item: TypeSensorDO): void => {
    window.store.dispatch(removeTab('STE'));
    const rowId: number = item?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: TypeSensorDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.name}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        typeSensor('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load();
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public onList = (item: TypeSensorDO): void => {
    window.store.dispatch(removeTab('STV'));
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadTabVariables(rowId);
    }, 0);
  };

  public loadTabVariables = (rowId: number): void => {
    const tabId: string = 'STV';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: `Variables | Sensor Type ID: ${rowId}`,
      component: Variables,
      props: {
        rowId,
        tabId: this.pid,
        report: 'SENSOR TYPE VARIABLES',
        title: 'Sensor Type Variables',
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      code: 'Code',
      name: 'Name',
      type: 'Type',
      vers: 'Version',
      active: 'Active',
      typeModuleName: 'Module Type',
      Actions: 'Action Buttons',
    };
    const columnsToHide: string[] = [
      'typeModuleId'
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      code: { textAlign: 'right' },
      name: { textAlign: 'left' },
      type: { textAlign: 'left' },
      vers: { textAlign: 'right' },
      active: { textAlign: 'center' },
      typeModuleName: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          onList={this.onList}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
